import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import BrandLogo from "./BrandLogo";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About Us" },
  { to: "/services", label: "Services" },
  { to: "/testimonials", label: "Testimonials" },
  { to: "/contact", label: "Contact Us" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  return (
    <header
      className={`sticky top-0 z-40 w-full transition-all duration-300 ${
        scrolled
          ? "border-b border-slate-200/80 bg-white/85 backdrop-blur-md shadow-[0_6px_24px_-12px_rgba(10,37,64,0.18)]"
          : "bg-white"
      }`}
      data-testid="main-navbar"
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <Link to="/" data-testid="nav-home-logo">
          <BrandLogo />
        </Link>
        <nav className="hidden items-center gap-1 lg:flex">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              className={({ isActive }) =>
                `relative rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                  isActive
                    ? "text-[#0A2540]"
                    : "text-slate-600 hover:text-[#0A2540]"
                }`
              }
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {({ isActive }) => (
                <span className="relative">
                  {l.label}
                  {isActive ? (
                    <span
                      className="absolute -bottom-1 left-1/2 h-[3px] w-6 -translate-x-1/2 rounded-full"
                      style={{ background: "#10B981" }}
                    />
                  ) : null}
                </span>
              )}
            </NavLink>
          ))}
        </nav>
        <a
          href="tel:+18774691642"
          className="hidden items-center gap-2 rounded-full px-3 py-2 text-sm font-medium text-slate-700 hover:text-[#0A2540] lg:inline-flex"
          data-testid="nav-phone"
        >
          <Phone className="h-4 w-4" /> +1 (877) 469-1642
        </a>
        <button
          className="inline-flex items-center justify-center rounded-md p-2 text-slate-700 lg:hidden"
          aria-label="Toggle menu"
          onClick={() => setMobileOpen((v) => !v)}
          data-testid="nav-mobile-toggle"
        >
          {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>
      {/* Mobile menu */}
      {mobileOpen ? (
        <div className="border-t border-slate-200 bg-white lg:hidden" data-testid="nav-mobile-menu">
          <div className="mx-auto flex max-w-7xl flex-col gap-1 px-4 py-3">
            <a
              href="tel:+18774691642"
              className="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-slate-700"
            >
              <Phone className="h-4 w-4" /> +1 (877) 469-1642
            </a>
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                className={({ isActive }) =>
                  `rounded-lg px-3 py-2 text-sm font-medium ${
                    isActive ? "bg-[#0A2540]/5 text-[#0A2540]" : "text-slate-700"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </div>
        </div>
      ) : null}
    </header>
  );
};

export default Navbar;
